import {gql} from "@apollo/client";
import {AnticipatedDischargeDateUpdateInput} from "../../domain";
import {getDomainGraphqlError, useMutation$} from "graphqlsupport";
import {useCallback} from "react";
import {format} from "date-fns-tz";
import {LOCAL_DATE_FORMAT, showToast} from "common";


const GRAPHQL_MUTATION = gql`
mutation submitAnticipatedDischargeDateUpdate($input: AuthAnticipatedDischargeDateUpdateInput) {
    submitAuthAnticipatedDischargeUpdate(input: $input) {
        guid
    }
}`;

type AuthAnticipatedDischargeDateUpdateInput = AnticipatedDischargeDateUpdateInput & {
    authorizationGuid: string;
}

const sanitizePayload = (formValues: AnticipatedDischargeDateUpdateInput, authorizationGuid: string): AuthAnticipatedDischargeDateUpdateInput => {
    const submittedAdDate = formValues.submittedAnticipatedDischargeDate ?
        format(new Date(formValues.submittedAnticipatedDischargeDate), LOCAL_DATE_FORMAT) : '';
    const dateDischargeDiscussed = formValues.dateDischargeDiscussed ?
        format(new Date(formValues.dateDischargeDiscussed), LOCAL_DATE_FORMAT) : '';
    const dateOfLastPlannedSession = formValues.dateOfLastPlannedSession ?
        format(new Date(formValues.dateOfLastPlannedSession), LOCAL_DATE_FORMAT) : '';
    
    const input: AnticipatedDischargeDateUpdateInput = {
        newAnticipatedDischargeDateDefined: 'YES', // FORCE ALWAYS SET The newAnticipatedDischargeDateDefined
        clinicalRationale: formValues.clinicalRationale,
        reasonForDischarge: formValues.reasonForDischarge,
        dischargeInitiator: formValues.dischargeInitiator,
        clientFamilyAgreesWithDischarge: formValues.clientFamilyAgreesWithDischarge,
        dischargeReportAcknowledgement: formValues.dischargeReportAcknowledgement,
    }
    
    if (submittedAdDate !== '') {
        input.submittedAnticipatedDischargeDate = submittedAdDate;
    }
    if (dateDischargeDiscussed !== '') {
        input.dateDischargeDiscussed = dateDischargeDiscussed;
    }
    if (dateOfLastPlannedSession !== '') {
        input.dateOfLastPlannedSession = dateOfLastPlannedSession;
    }
    if (formValues.clientFamilyAgreesWithDischarge === 'NO' && formValues.reasonForDisagreement) {
        input.reasonForDisagreement = formValues.reasonForDisagreement;
    }

    return {
        ...input,
        authorizationGuid,
    }
}

export function useAuthAnticipatedDischargeDateUpdate(authorizationGuid: string) {
    const {execute,} = useMutation$<{ input: AuthAnticipatedDischargeDateUpdateInput }, void>(GRAPHQL_MUTATION);
    const submitAuthAnticipatedDischargeDateUpdate = useCallback(async function (formValues: AnticipatedDischargeDateUpdateInput | null) {
        if (formValues == null) {
            return;
        }

        try {
            await execute({
                variables: {
                    input: sanitizePayload(formValues, authorizationGuid),
                }
            });
            showToast.success(
                'Discharge Notification request successfully submitted. The Request can be tracked in the "Submitted Requests" section and a request number ID will be available shortly.'
            );
        } catch (errors: any) {
            if (getDomainGraphqlError(errors)) {
                showToast.error(getDomainGraphqlError(errors));
            } else {
                showToast.error(`Unable to submit Discharge notification: ${errors.message || errors[0].message}`);
            }
        }
    }, [authorizationGuid, execute]);

    return {
        submitAuthAnticipatedDischargeDateUpdate
    }
}
